/**
 * Lyric Boost Design System - Typography Tokens
 * 
 * Typography scale and styling definitions for consistent text rendering.
 * Includes font families, sizes, weights, and line heights.
 */

export const typography = {
  // === Font Family ===
  fontFamily: {
    primary: "'Poppins', sans-serif",
    monospace: 'monospace',
  },

  // === Font Weights ===
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // === Font Sizes ===
  fontSize: {
    // Hero and display text
    hero: {
      mobile: '2rem',      // 32px
      tablet: '3rem',      // 48px
      desktop: '4rem',     // 64px
    },
    
    // Headings
    h1: {
      mobile: '1.75rem',   // 28px
      desktop: '2.5rem',   // 40px
    },
    h2: {
      mobile: '1.5rem',    // 24px
      desktop: '2rem',     // 32px
    },
    h3: {
      mobile: '1.25rem',   // 20px
      desktop: '1.75rem',  // 28px
    },
    h4: {
      mobile: '1.125rem',  // 18px
      desktop: '1.5rem',   // 24px
    },
    h5: {
      mobile: '1rem',      // 16px
      desktop: '1.25rem',  // 20px
    },
    h6: {
      mobile: '0.875rem',  // 14px
      desktop: '1rem',     // 16px
    },

    // Body text
    body: {
      large: '1.125rem',   // 18px
      default: '1rem',     // 16px
      small: '0.875rem',   // 14px
    },

    // Labels and UI elements
    label: {
      large: '0.95rem',    // 15.2px
      default: '0.875rem', // 14px
      small: '0.75rem',    // 12px
    },

    // Captions and helper text
    caption: '0.75rem',    // 12px
    
    // Overline text
    overline: '0.75rem',   // 12px
  },

  // === Line Heights ===
  lineHeight: {
    tight: 1.1,
    snug: 1.2,
    normal: 1.5,
    relaxed: 1.6,
    loose: 1.8,
  },

  // === Letter Spacing ===
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
} as const;

/**
 * Typography variants combining all properties
 * These map to common usage patterns in the app
 */
export const typographyVariants = {
  // Hero text for landing pages
  hero: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.hero,
    lineHeight: typography.lineHeight.snug,
  },

  // Page headings
  pageHeading: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.h5,
    lineHeight: typography.lineHeight.snug,
  },

  // Section headings
  sectionHeading: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.h6,
    lineHeight: typography.lineHeight.snug,
  },

  // Subsection headings
  subsectionHeading: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.body.default,
    lineHeight: typography.lineHeight.normal,
  },

  // Body text
  body: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.body.default,
    lineHeight: typography.lineHeight.normal,
  },

  // Body text (large variant)
  bodyLarge: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.body.large,
    lineHeight: typography.lineHeight.relaxed,
  },

  // Body text (small variant)
  bodySmall: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.body.small,
    lineHeight: typography.lineHeight.normal,
  },

  // Labels (for form inputs, etc.)
  label: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.label.default,
    lineHeight: typography.lineHeight.normal,
  },

  // Labels (small variant)
  labelSmall: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.label.small,
    lineHeight: typography.lineHeight.normal,
  },

  // Caption text
  caption: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.caption,
    lineHeight: typography.lineHeight.normal,
  },

  // Helper text
  helper: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.label.small,
    lineHeight: typography.lineHeight.normal,
  },

  // Overline text (uppercase labels)
  overline: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.overline,
    lineHeight: typography.lineHeight.normal,
    textTransform: 'uppercase' as const,
    letterSpacing: typography.letterSpacing.wider,
  },

  // Button text
  button: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.body.default,
    lineHeight: typography.lineHeight.normal,
    textTransform: 'none' as const,
  },
} as const;

// Type exports
export type Typography = typeof typography;
export type TypographyVariants = typeof typographyVariants;
